import React, { useState, useEffect, useRef } from "react";
import { functions, db } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { doc, getDoc, onSnapshot } from "firebase/firestore";

const ASSETS = [
  { symbol: "XAUUSD", name: "Gold", category: "Commodities", tvSymbol: "OANDA:XAUUSD", lotSize: 100 },
  { symbol: "XAGUSD", name: "Silver", category: "Commodities", tvSymbol: "OANDA:XAGUSD", lotSize: 50 },{ symbol: "XPTUSD", name: "Platinum", category: "Commodities", tvSymbol: "TVC:PLATINUM", lotSize: 100 },
  { symbol: "NATGAS", name: "Natural Gas", category: "Commodities", tvSymbol: "NYMEX:NG1!", lotSize: 100 },
  { symbol: "COPPER", name: "Copper", category: "Commodities", tvSymbol: "COMEX:HG1!", lotSize: 100 },
  { symbol: "COCOA", name: "Cocoa", category: "Commodities", tvSymbol: "ICEUS:CC1!", lotSize: 100 },
  { symbol: "CORN", name: "Corn", category: "Commodities", tvSymbol: "CBOT:ZC1!", lotSize: 100 },
  { symbol: "USOIL", name: "Oil (WTI)", category: "Commodities", tvSymbol: "TVC:USOIL", lotSize: 100 },
  { symbol: "BTCUSD", name: "Bitcoin", category: "Crypto", tvSymbol: "BITSTAMP:BTCUSD", lotSize: 1 },
  { symbol: "ETHUSD", name: "Ethereum", category: "Crypto", tvSymbol: "BITSTAMP:ETHUSD", lotSize: 1 },
  { symbol: "ADAUSD", name: "Cardano", category: "Crypto", tvSymbol: "BINANCE:ADAUSDT", lotSize: 1 },
  { symbol: "BNBUSD", name: "BNB", category: "Crypto", tvSymbol: "BINANCE:BNBUSDT", lotSize: 1 },
  { symbol: "SOLUSD", name: "Solana", category: "Crypto", tvSymbol: "BINANCE:SOLUSDT", lotSize: 1 },
  { symbol: "EURUSD", name: "EUR/USD", category: "Forex", tvSymbol: "OANDA:EURUSD", lotSize: 100000 },
  { symbol: "GBPUSD", name: "GBP/USD", category: "Forex", tvSymbol: "OANDA:GBPUSD", lotSize: 100000 },
  { symbol: "USDJPY", name: "USD/JPY", category: "Forex", tvSymbol: "OANDA:USDJPY", lotSize: 100000 },
  { symbol: "AUDUSD", name: "AUD/USD", category: "Forex", tvSymbol: "OANDA:AUDUSD", lotSize: 100000 },
  { symbol: "USDCAD", name: "USD/CAD", category: "Forex", tvSymbol: "OANDA:USDCAD", lotSize: 100000 },
  { symbol: "USDCHF", name: "USD/CHF", category: "Forex", tvSymbol: "OANDA:USDCHF", lotSize: 100000 },
  { symbol: "AAPL", name: "Apple", category: "Stocks", tvSymbol: "NASDAQ:AAPL", lotSize: 100 },
  { symbol: "TSLA", name: "Tesla", category: "Stocks", tvSymbol: "NASDAQ:TSLA", lotSize: 100 },
  { symbol: "GOOGL", name: "Google", category: "Stocks", tvSymbol: "NASDAQ:GOOGL", lotSize: 100 },
  { symbol: "AMZN", name: "Amazon", category: "Stocks", tvSymbol: "NASDAQ:AMZN", lotSize: 100 },
  { symbol: "MSFT", name: "Microsoft", category: "Stocks", tvSymbol: "NASDAQ:MSFT", lotSize: 100 },
  { symbol: "NVDA", name: "NVIDIA", category: "Stocks", tvSymbol: "NASDAQ:NVDA", lotSize: 100 },
];

const CATEGORIES = ["All", "Commodities", "Crypto", "Forex", "Stocks"];
const LOT_SIZES = [0.01, 0.10, 0.50, 1.00, 5.00, 10.00];
const COMMODITIES = ["XAUUSD", "XAGUSD", "USOIL", "XPTUSD", "NATGAS", "COPPER", "COCOA", "CORN"];

const FALLBACK_PRICES = {
  XAUUSD: 3320, XAGUSD: 33, USOIL: 75, XPTUSD: 980, NATGAS: 2.8, COPPER: 4.2, COCOA: 8500, CORN: 4.5,
  AAPL: 175, TSLA: 180, GOOGL: 160, AMZN: 185, MSFT: 415, NVDA: 850
};

export default function Market({ user, wallet }) {
  const [selected, setSelected] = useState(ASSETS[0]);
  const [category, setCategory] = useState("All");
  const [showModal, setShowModal] = useState(false);
  const [direction, setDirection] = useState("buy");
  const [lots, setLots] = useState(0.01);
  const [customLots, setCustomLots] = useState("");
  const [leverage, setLeverage] = useState(100);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [entryPrice, setEntryPrice] = useState(0);
  const [priceLoading, setPriceLoading] = useState(false);
  const [adminPrices, setAdminPrices] = useState({});
  const priceRef = useRef(0);
  const intervalRef = useRef(null);

  const filtered = category === "All" ? ASSETS : ASSETS.filter(a => a.category === category);
  const activeLots = customLots !== "" ? Number(customLots) : lots;
  const contractSize = selected.lotSize;
  const positionValue = activeLots * contractSize * (entryPrice || 1);
  const margin = positionValue / leverage;
  const spread = 0.1;
  const spreadCost = spread * activeLots;
  const decimals = entryPrice < 10 ? 4 : 2;
  const leverageLabel = leverage === 99999 ? "Unlimited" : `1:${leverage}`;
  const isAdminPrice = COMMODITIES.includes(selected.symbol) && adminPrices[selected.symbol]?.price > 0;

  useEffect(() => {
    const fetchLeverage = async () => {
      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists() && snap.data().leverage) {
          setLeverage(snap.data().leverage);
        }
      } catch (e) {}
    };
    fetchLeverage();
  }, [user]);

  // Listen to admin prices in real-time
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "settings", "prices"), (snap) => {
      if (snap.exists()) {
        setAdminPrices(snap.data());
      }
    });
    return unsub;
  }, []);

  // Update price when admin price or selected asset changes
  useEffect(() => {
    if (COMMODITIES.includes(selected.symbol) && adminPrices[selected.symbol]?.price > 0) {
      const price = adminPrices[selected.symbol].price;
      setEntryPrice(price);
      priceRef.current = price;
      setPriceLoading(false);
    }
  }, [adminPrices, selected]);

  useEffect(() => {
    const fetchPrice = async () => {
      if (COMMODITIES.includes(selected.symbol)) {
        // Use admin price for commodities
        const adminPrice = adminPrices[selected.symbol]?.price;
        if (adminPrice > 0) {
          setEntryPrice(adminPrice);
          priceRef.current = adminPrice;
          setPriceLoading(false);
          return;
        }
        // Fallback if no admin price set
        const fallback = FALLBACK_PRICES[selected.symbol];
        setEntryPrice(fallback);
        priceRef.current = fallback;
        setPriceLoading(false);
        return;
      }

      try {
        let price = 0;
        const cryptoMap = {
          BTCUSD: "bitcoin", ETHUSD: "ethereum", ADAUSD: "cardano",
          BNBUSD: "binancecoin", SOLUSD: "solana"
        };
        const forexMap = {
          EURUSD: "EUR", GBPUSD: "GBP", USDJPY: "JPY",
          AUDUSD: "AUD", USDCAD: "CAD", USDCHF: "CHF"
        };

        if (cryptoMap[selected.symbol]) {
          const res = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${cryptoMap[selected.symbol]}&vs_currencies=usd`);
          const data = await res.json();
          price = data[cryptoMap[selected.symbol]]?.usd || 0;
        } else if (forexMap[selected.symbol]) {
          const base = selected.symbol.slice(0, 3);
          const quote = selected.symbol.slice(3, 6);
          const res = await fetch(`https://api.exchangerate-api.com/v4/latest/${base}`);
          const data = await res.json();
          price = base === "USD" ? data.rates[quote] : data.rates["USD"];
        } else {
          price = FALLBACK_PRICES[selected.symbol] || 100;
        }

        if (price > 0) {
          setEntryPrice(price);
          priceRef.current = price;
          setPriceLoading(false);
        }
      } catch (e) {
        const fallback = FALLBACK_PRICES[selected.symbol];
        if (fallback && !priceRef.current) {
          setEntryPrice(fallback);
          priceRef.current = fallback;
          setPriceLoading(false);
        }
      }
    };

    setPriceLoading(true);
    setEntryPrice(0);
    priceRef.current = 0;
    fetchPrice();

    if (intervalRef.current) clearInterval(intervalRef.current);

    const isCrypto = ["BTCUSD","ETHUSD","ADAUSD","BNBUSD","SOLUSD"].includes(selected.symbol);
    const isForex = ["EURUSD","GBPUSD","USDJPY","AUDUSD","USDCAD","USDCHF"].includes(selected.symbol);
    const isCommodity = COMMODITIES.includes(selected.symbol);

    if (isCommodity) {
      intervalRef.current = setInterval(fetchPrice, 5000);
    } else if (isCrypto) {
      intervalRef.current = setInterval(fetchPrice, 3000);
    } else if (isForex) {
      intervalRef.current = setInterval(fetchPrice, 10000);
    } else {
      intervalRef.current = setInterval(fetchPrice, 30000);
    }

    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [selected, adminPrices]);

  const handleTrade = async () => {
    if (!activeLots || activeLots <= 0) { setMsg("Enter valid lot size"); return; }
    if (margin > (wallet?.balance || 0)) { setMsg(`Insufficient balance. Required margin: $${margin.toFixed(2)}`); return; }
    if (!priceRef.current) { setMsg("Price not available, please wait"); return; }
    setLoading(true); setMsg("");
    try {
      const placeTrade = httpsCallable(functions, "placeTrade");
      await placeTrade({
        asset: selected.symbol,
        direction,
        amount: parseFloat(margin.toFixed(2)),
        leverage,
        entryPrice: priceRef.current,
        lots: activeLots,
        contractSize,
        positionValue: parseFloat(positionValue.toFixed(2)),
      });
      setMsg(`✓ Trade opened! ${activeLots} lot ${selected.name} @ $${priceRef.current.toFixed(decimals)}`);
      setTimeout(() => { setShowModal(false); setMsg(""); }, 2000);
    } catch (e) {
      setMsg("Error: " + e.message);
    }
    setLoading(false);
  };

  return (
    <div className="market-page">
      <div className="market-left">
        <div className="asset-filter">
          {CATEGORIES.map(c => (
            <div key={c} className={`filter-btn ${category === c ? "active" : ""}`} onClick={() => setCategory(c)}>{c}</div>
          ))}
        </div>
        <div className="asset-list">
          {filtered.map(a => (
            <div key={a.symbol} className={`asset-item ${selected.symbol === a.symbol ? "active" : ""}`} onClick={() => setSelected(a)}>
              <div className="asset-item-name">{a.name}</div>
              <div className="asset-item-symbol">{a.symbol}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="market-right">
        <div className="market-topbar">
          <div>
            <div className="market-asset-name">{selected.name}</div>
            <div className="market-asset-symbol">
              {selected.symbol} • {selected.category}
              {priceLoading
                ? <span style={{color:"#5A6480", marginLeft:"12px"}}>Loading...</span>
                : entryPrice > 0
                  ? <span style={{color:"#F0A500", marginLeft:"12px", fontFamily:"monospace"}}>
                      ${entryPrice.toFixed(decimals)}
                    </span>
                  : null
              }
              {isAdminPrice && (
                <span style={{marginLeft:"8px", background:"rgba(0,200,150,0.12)", color:"#00C896", fontSize:"9px", padding:"2px 6px", borderRadius:"3px", fontWeight:"700"}}>
                  LIVE
                </span>
              )}
              <span style={{color:"#5A6480", marginLeft:"8px", fontSize:"10px"}}>Spread: {spread}</span>
            </div>
          </div>
          <div style={{display:"flex", alignItems:"center", gap:"12px"}}>
            <div style={{fontSize:"11px", color:"#5A6480"}}>
              Leverage: <span style={{color:"#F0A500", fontWeight:"700"}}>{leverageLabel}</span>
            </div>
            <button className="btn-trade" onClick={() => { setShowModal(true); setMsg(""); }}>Trade Now</button>
          </div>
        </div>
        <div className="market-chart">
          <iframe
            src={`https://s.tradingview.com/widgetembed/?symbol=${selected.tvSymbol}&interval=15&theme=dark&style=1`}
            width="100%"
            height="100%"
            frameBorder="0"
            title="chart"
          ></iframe>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()} style={{width:"400px"}}>
            <div className="modal-header">
              <div className="modal-title">Trade {selected.name}</div>
              <div className="modal-close" onClick={() => setShowModal(false)}>✕</div>
            </div>

            <div style={{display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"8px", marginBottom:"12px"}}>
              <div style={{background:"#0B0E14", borderRadius:"8px", padding:"10px"}}>
                <div style={{fontSize:"9px", color:"#5A6480", letterSpacing:"1px", textTransform:"uppercase"}}>Balance</div>
                <div style={{fontSize:"13px", fontWeight:"700", color:"#F0A500", fontFamily:"monospace"}}>${wallet?.balance?.toFixed(2) || "0.00"}</div>
              </div>
              <div style={{background:"#0B0E14", borderRadius:"8px", padding:"10px"}}>
                <div style={{fontSize:"9px", color:"#5A6480", letterSpacing:"1px", textTransform:"uppercase"}}>Entry Price</div>
                <div style={{fontSize:"13px", fontWeight:"700", color:"#E8EAF0", fontFamily:"monospace"}}>${entryPrice.toFixed(decimals)}</div>
              </div>
              <div style={{background:"#0B0E14", borderRadius:"8px", padding:"10px"}}>
                <div style={{fontSize:"9px", color:"#5A6480", letterSpacing:"1px", textTransform:"uppercase"}}>Leverage</div>
                <div style={{fontSize:"13px", fontWeight:"700", color:"#F0A500"}}>{leverageLabel}</div>
              </div>
            </div>

            <div className="direction-row">
              <div className={`dir-btn buy ${direction === "buy" ? "active" : ""}`} onClick={() => setDirection("buy")}>▲ BUY</div>
              <div className={`dir-btn sell ${direction === "sell" ? "active" : ""}`} onClick={() => setDirection("sell")}>▼ SELL</div>
            </div>

            <label className="dep-label">Lot Size</label>
            <div className="leverage-row" style={{marginBottom:"6px"}}>
              {LOT_SIZES.map(l => (
                <div key={l} className={`lev-btn ${lots === l && customLots === "" ? "active" : ""}`}
                  onClick={() => { setLots(l); setCustomLots(""); }}>
                  {l}
                </div>
              ))}
            </div>
            <input
              className="inp"
              type="number"
              placeholder="Custom lot (e.g. 0.03)"
              value={customLots}
              onChange={e => setCustomLots(e.target.value)}
              step="0.01"
              min="0.01"
            />

            <div style={{background:"#0B0E14", borderRadius:"8px", padding:"12px", marginTop:"8px"}}>
              <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px", fontSize:"12px"}}>
                <div>
                  <div style={{color:"#5A6480", fontSize:"10px", textTransform:"uppercase", letterSpacing:"1px"}}>Lots</div>
                  <div style={{fontFamily:"monospace", fontWeight:"700", color:"#E8EAF0"}}>{activeLots}</div>
                </div>
                <div>
                  <div style={{color:"#5A6480", fontSize:"10px", textTransform:"uppercase", letterSpacing:"1px"}}>Contract Size</div>
                  <div style={{fontFamily:"monospace", fontWeight:"700", color:"#E8EAF0"}}>{contractSize.toLocaleString()}</div>
                </div>
                <div>
                  <div style={{color:"#5A6480", fontSize:"10px", textTransform:"uppercase", letterSpacing:"1px"}}>Position Value</div>
                  <div style={{fontFamily:"monospace", fontWeight:"700", color:"#F0A500"}}>${positionValue.toFixed(2)}</div>
                </div>
                <div>
                  <div style={{color:"#5A6480", fontSize:"10px", textTransform:"uppercase", letterSpacing:"1px"}}>Required Margin</div>
                  <div style={{fontFamily:"monospace", fontWeight:"700", color: margin > (wallet?.balance || 0) ? "#FF3B5C" : "#00C896"}}>${margin.toFixed(2)}</div>
                </div>
                <div>
                  <div style={{color:"#5A6480", fontSize:"10px", textTransform:"uppercase", letterSpacing:"1px"}}>Spread</div>
                  <div style={{fontFamily:"monospace", color:"#5A6480"}}>{spread}</div>
                </div>
                <div>
                  <div style={{color:"#5A6480", fontSize:"10px", textTransform:"uppercase", letterSpacing:"1px"}}>Spread Cost</div>
                  <div style={{fontFamily:"monospace", color:"#5A6480"}}>${spreadCost.toFixed(4)}</div>
                </div>
              </div>
            </div>

            {msg && (
              <div className="dep-msg" style={{color: msg.includes("✓") ? "#00C896" : "#FF3B5C", background: msg.includes("✓") ? "rgba(0,200,150,0.08)" : "rgba(255,59,92,0.08)"}}>
                {msg}
              </div>
            )}

            <button
              className={`btn-main btn-full ${direction === "sell" ? "btn-sell" : ""}`}
              onClick={handleTrade}
              disabled={loading || margin > (wallet?.balance || 0)}
              style={{marginTop:"8px"}}
            >
              {loading ? "Processing..." : `${direction === "buy" ? "▲ BUY" : "▼ SELL"} ${activeLots} Lot ${selected.name}`}
            </button>

            <div style={{fontSize:"10px", color:"#3A4460", textAlign:"center", marginTop:"8px"}}>
              ⚠️ Change leverage in Settings page. Current: {leverageLabel}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}